import React, {
  Dispatch,
  FC,
  memo,
  ReactElement,
  SetStateAction,
  Suspense,
  SyntheticEvent,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import { addBetSlipEvent } from 'utils/betslip/betslip';
import { arrayFindBy } from 'utils/collection-manipulation/array-find-by';
import {
  sortByOrder,
  sortMarketsByMainOrder
} from 'utils/collection-manipulation/array-sort-by';
import { EContainerIconSize, EIconSize } from 'utils/constants/app/ui';
import { timeoutDuration } from 'utils/constants/dateInfo/time-numbers';
import { WINNER_MARKETS } from 'utils/constants/sportsbook/grouped-market-names';
import {
  ArcheryH2HSportAlias,
  CompoundArcherySportAlias,
  NotRealSportsForDifFormats
} from 'utils/constants/sportsbook/sport-list';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { ADD_GAME_CARDS } from 'utils/constants/tour';
import { dateFormat } from 'utils/date-manipulation/date-format';
import { lazy } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { checkIsNotESportType } from 'utils/sportsbook/check-type-is-not-sportbook';
import {
  getEsportCurrentMapName,
  getGameCurrentState
} from 'utils/sportsbook/game-current-state';
import { hidePrivateCompetitions } from 'utils/sportsbook/hide-private-competitions';
import { shouldShowLiveTime } from 'utils/sportsbook/live-games-helper';
import { SportType } from 'utils/sportsbook/sport-data';
import { BetSlipEventObj } from 'interfaces/bet-data';
import { ETeamNumber } from 'interfaces/sportsbook';
import { Event, Game, Market } from 'interfaces/sportsbook-data-levels';
import { AFTER_SIGN_IN_ACTION_TYPES } from 'services/afterSignIn';
import { DesktopESportMatchCardLine } from './DesktopESportMatchCardLine';
import { Container, Header, Team, Title, Wrapper } from './style';
import { EsportPageType } from 'newelements/Sportsbook/Esport/helpers/getGameTypeByPageType';
import { EventAction } from 'components/EventAction';
import { FavoriteButtonGame } from 'components/FavoriteButton';
import { GlobalIcon } from 'components/GlobalIcon';
import { ImageOverlay } from 'components/ImageOverlay';
import { MarketsCount } from 'components/MarketsCount';
import { Odds } from 'components/Odds';
import { Pill } from 'components/Pill';
import { TeamCaption } from 'components/TeamCaption';
import { MatchStatistic } from 'newcomponents/Shared/sportsbook/MatchStatistic';
import { NotificationIcon } from 'newcomponents/Shared/sportsbook/NotificationIcon';
import { VideoCamIcon } from 'newcomponents/Shared/sportsbook/VideoCamIcon';
import { Col } from 'newcomponents/UI/Col';
import { Row } from 'newcomponents/UI/Row';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useGameScrollIntoView } from 'hooks/useGameScrollIntoView';
import { useOddChange } from 'hooks/useOddChange';
import { useSearchParams } from 'hooks/useSearchParams';
import { useSbRouteMatch } from 'newhooks/generic/useSbRouteMatch';
import { useOpenBetBuilder } from 'newhooks/sportsbook/useOpenBetBuilder';
import { useSportName } from 'providers/SportDataProvider';
import { getEventIds, getOddFormat } from 'store/selectors/betslip';
import { getBetslip } from 'store/selectors/spec-elems';
import { getMarketType, getScrollToGameId } from 'store/selectors/sport-data';
import { getIsLoggedIn } from 'store/selectors/user-data';
import './index.less';

const { CustomDataPicker } = lazy(
  () =>
    import(
      /* webpackChunkName: "custom-data-picker" */ 'components/CustomDataPicker'
    )
);

export type TMatchCardInlineProps = {
  game: Game;
  sportId?: number;
  currentSport: string;
  competitionName: string;
  sportName?: string;
  regionName?: string;
  onClick: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  configs?: {
    category: 'sport' | 'esport';
  };
  selectedGames?: boolean;
  bgImg?: string;
  calendar?: boolean;
  hideWrapper?: boolean;
  longDate?: boolean;
  noNeedScroll?: boolean;
  showSportIcon?: boolean;
  hideCompetitionLogo?: boolean;
  isActive?: boolean;
  isLive?: boolean;
  competitionId?: number;
  regionAlias?: string;
  showExpandedMarkets?: boolean;
  setShowExpandedMarkets?: Dispatch<SetStateAction<boolean>>;
};

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

export const MatchCardInline: FC<TMatchCardInlineProps> = memo(props => {
  const isLoggedIn = useSelector(getIsLoggedIn);
  const scrollToGameId = useSelector(getScrollToGameId);
  const betslipPresent = useSelector(getBetslip);
  const oddFormat = useSelector(getOddFormat);
  const marketType = useSelector(getMarketType);
  const eventIds = useSelector(getEventIds);

  const scrollToRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const handleScroll = useGameScrollIntoView();
  const sportData = useSportName();
  const { params } = useSbRouteMatch();
  const { region } = params;
  const { type } = useSearchParams() as { type: EsportPageType };

  const [selected, setSelected] = useState<string[]>([]);
  const [notMainMarket, setNotMainMarket] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [marketGroupID, setMarketGroupID] = useState<number | null>(null);

  const isPrematchEsportsAll =
    type === 'prematch' && params.sport === 'AllESports';

  const gameNumber = SpringConfigs.GAME_NUMBER;

  const {
    game: gameObj = {} as Game,
    onClick,
    currentSport,
    regionName,
    sportName,
    competitionName,
    selectedGames,
    bgImg,
    configs = { category: 'sport' },
    calendar,
    hideWrapper,
    longDate,
    noNeedScroll,
    competitionId,
    sportId,
    regionAlias
  } = props;

  const game = useMemo(
    () =>
      isLoggedIn ? gameObj : hidePrivateCompetitions(gameObj, competitionId),
    [gameObj, competitionId, isLoggedIn]
  );

  const isActive = useMemo(
    () => String(params.game) === String(game?.id) || props.isActive,
    [params.game, game?.id, props.isActive]
  );

  const markets = useMemo(
    () =>
      Object.values(game.market || {})
        .sort((a, b) => a.group_id - b.group_id)
        .reduce((result: Market[], market) => {
          const isGroupExist = !!result.find(
            ({ group_id }) => group_id === market.group_id
          );

          if (!isGroupExist) {
            result.push(market);
          }

          return result;
        }, []),
    [game]
  );

  const [marketName, setMarketName] = useState(markets[0]?.group_name);

  const isLive = game.type ? game.type === 1 : props.isLive;
  const isOutRight = game.show_type === 'OUTRIGHT';
  const longFormatChecker = NotRealSportsForDifFormats.includes(
    params.category
  );

  const showBetBuilder =
    !isLive &&
    !calendar &&
    game.sportcast_id &&
    SpringConfigs.BET_BUILDER_SWITCHER;

  const betBuilderProps = {
    sportId,
    fixtureId: game.sportcast_id,
    gameTitle: `${game.team1_name} - ${game.team2_name}`,
    afterSignInActonName: `MatchCardInline_${game.id}_${AFTER_SIGN_IN_ACTION_TYPES.TOGGLE_BET_BUILDER_MODAL}`
  };

  const { openBetBuilder } = useOpenBetBuilder(betBuilderProps);

  const isGamesWithArcheryNewData = [
    ArcheryH2HSportAlias,
    CompoundArcherySportAlias
  ].includes(currentSport);

  const shouldScrollIntoView = useMemo(
    () =>
      !noNeedScroll &&
      (configs.category !== SportType.ESPORT || !isMobile()) &&
      game?.id &&
      scrollToGameId?.id === game?.id?.toString(),
    [game?.id, scrollToGameId?.id]
  );

  const iconSize =
    configs.category === SportType.ESPORT
      ? EContainerIconSize.MIDDLE
      : EContainerIconSize.SMALL;

  const filteredMarkets = useMemo(() => {
    return markets.map(val => {
      return {
        label: val.group_name,
        value: val.group_id
      };
    });
  }, [markets]);

  const selectedGroupName = useMemo(() => {
    return filteredMarkets.find(
      marketItem => marketItem.value === marketGroupID
    );
  }, [marketGroupID, filteredMarkets]);

  const startTime = useMemo(() => {
    return longDate || longFormatChecker
      ? dayjs.unix(game.start_ts).format(
          dateFormat({
            date: SpringConfigs.DT.shortDate,
            time: SpringConfigs.DT.time,
            dateTimeSeparator: ' | '
          })
        )
      : dayjs.unix(game.start_ts).format(SpringConfigs.DT.time);
  }, [game.start_ts, longDate, longFormatChecker]);

  const isSportIconVisible = !!(
    props.showSportIcon &&
    (currentSport || game.sport_alias)
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    if (clicked) {
      timeout = setTimeout(() => {
        setClicked(false);
      }, timeoutDuration.DURATION_800);
    }

    return () => {
      timeout && clearTimeout(timeout);
    };
  }, [clicked]);

  useEffect(() => {
    if (marketGroupID === null && markets.length > 0) {
      setMarketGroupID(markets[0]?.group_id);
    }

    if (marketGroupID !== null && markets.length === 0) {
      setMarketGroupID(null);
    }

    if (
      marketGroupID !== null &&
      markets.length > 0 &&
      !markets.some(market => market.group_id === marketGroupID)
    ) {
      setMarketGroupID(markets[0]?.group_id);
    }
  }, [markets]);

  const sortedMarkets: Market[] = useMemo(() => {
    if (game.market) {
      const mainMarkets = sortByOrder(Object.values(game.market))?.filter(
        elem =>
          WINNER_MARKETS.includes(elem?.type as string) ||
          configs.category === SportType.ESPORT
      );

      if (!mainMarkets.length) {
        setNotMainMarket(true);

        return sortMarketsByMainOrder(Object.values(game.market));
      }

      setNotMainMarket(false);

      return mainMarkets;
    }

    return [];
  }, [game.market, marketType?.value]);

  const addToBetSlip = (eventId: string | number) => {
    const market =
      sortedMarkets.find(market => market?.event?.[eventId]) ||
      sortedMarkets[0];

    const event = market?.event?.[eventId];

    addBetSlipEvent(
      {
        sport_id: sportId,
        marketType: market?.type || '',
        market: market,
        gameId: Number(game.id),
        eventId: Number(eventId),
        eventBase: event?.base,
        team1: game.team1_name,
        team2: game.team2_name || '',
        coeficient: event?.price,
        event_name: event?.name || '',
        game_start_date: game.start_ts,
        sport_index: (currentSport as string) || game?.sport_alias,
        sport_name: sportData || sportName || '',
        competition_name: competitionName || '',
        current_game_state: game.info?.current_game_state || '',
        current_game_time: game.info?.current_game_time || '',
        isLive,
        region_name: regionName as string,
        market_name: market?.name || '',
        game_name: `${game.team1_name} ${
          game.team2_name ? ` - ${game.team2_name}` : ''
        }`,
        game_info: game.info,
        express_id: market?.prematch_express_id || market?.express_id,
        type_1: event?.type_1
      } as BetSlipEventObj,
      true
    );
  };

  const { oddChangeHandler, popover, hidePopover } = useOddChange(
    setSelected,
    addToBetSlip,
    selected
  );

  const events = useMemo(() => {
    if (game.market && Object.keys(game.market).length) {
      const response = [];
      let eventsData: Record<string, Event> | undefined = {};

      if (isLive && isMobile() && !marketGroupID) {
        eventsData = sortedMarkets[0]?.event;
      } else if (marketType?.value && isMobile()) {
        eventsData = Object.values(sortedMarkets)[0].event;
      } else {
        eventsData = (
          arrayFindBy(
            sortedMarkets,
            'group_id',
            marketGroupID as number,
            false
          ) || sortedMarkets[0]
        )?.event;
      }

      for (const eventId in eventsData) {
        const event = eventsData[eventId];

        if (event) {
          response.push({
            value: eventId,
            coeficient: event.price,
            order: event.order,
            name: event.name,
            base: event.base ?? false,
            type1: event.type_1 ? event.type_1 : false,
            marketType: marketType?.value
          });
        }
      }

      return response.sort((a, b) => a.order - b.order);
    }

    return [];
  }, [game?.market, marketType?.value, marketGroupID, sortedMarkets]);

  useLayoutEffect(() => {
    if (eventIds?.length && events?.length) {
      setSelected([]);
      events.forEach(i => {
        if (
          eventIds.findIndex((id: number) => Number(id) === Number(i.value)) >=
          0
        ) {
          setSelected(prevSelected => [...prevSelected, i.value]);
        }
      });
    } else {
      setSelected([]);
    }
  }, [eventIds, events]);

  const gameCurrentState = useMemo(
    () =>
      game.info?.additional_data && !isGamesWithArcheryNewData
        ? getEsportCurrentMapName(game.info.additional_data, game.sport_alias)
        : getGameCurrentState(game.info || {}, currentSport),
    [
      game.info?.current_game_time,
      game.info?.current_game_state,
      game.info?.additional_data
    ]
  );

  useEffect(() => {
    setMarketName(selectedGroupName?.label || markets[0]?.group_name);
  }, [selectedGroupName, markets]);

  useEffect(() => {
    shouldScrollIntoView &&
      scrollToRef.current &&
      handleScroll(
        scrollToRef.current,
        'game',
        scrollToGameId?.top,
        undefined,
        { block: 'center' }
      );
  }, [shouldScrollIntoView]);

  const handleChangeOdds = (
    eventId: string,
    price: string,
    sp?: boolean | undefined,
    prevSp?: boolean | undefined
  ) => {
    if (
      !betslipPresent &&
      !SpringConfigs.IS_BETSLIP_FLOATING_ENABLED &&
      !isMobile()
    ) {
      onClick();
    }

    oddChangeHandler(eventId, Number(price), sp, prevSp);
  };

  const getSelectMarkets = (): ReactElement | null => {
    return configs.category === SportType.ESPORT &&
      markets.length &&
      markets.every(item => !!item.group_name) ? (
      <Suspense
        fallback={
          <Skeleton.Button
            style={{ width: isMobile() ? '90px' : '65px' }}
            size={isMobile() ? 'small' : 'default'}
          />
        }
      >
        <CustomDataPicker
          marketName={marketName}
          setMarketGroupID={setMarketGroupID}
          filteredMarkets={filteredMarkets}
        />
      </Suspense>
    ) : null;
  };

  return configs.category === SportType.ESPORT && !isMobile() ? (
    <DesktopESportMatchCardLine
      game={game}
      onClick={onClick}
      isLive={!!isLive}
      currentSport={currentSport}
      isOutRight={isOutRight}
      events={events}
      oddChangeHandler={oddChangeHandler}
      selectedOdd={selected}
      setMarketGroupID={setMarketGroupID}
      marketGroupID={marketGroupID}
      hideWrapper={hideWrapper}
      popover={popover}
      hidePopover={hidePopover}
      competitionName={competitionName}
      regionAlias={regionAlias}
    />
  ) : (
    <Container
      data-testid="e-sport-game"
      className={cc([
        'match-card__container',
        {
          'match-card__container--esport':
            configs.category === 'esport' && isMobile(),
          'match-card__container--esportPrematch':
            isMobile() && isPrematchEsportsAll
        }
      ])}
      {...(SpringConfigs.USER_PERSONALIZATION_ENABLED &&
      !!isActive &&
      !selectedGames
        ? { 'data-tour': ADD_GAME_CARDS }
        : {})}
      calendar={calendar}
      active={!!isActive && !selectedGames}
      selectedGames={selectedGames}
      bgImg={bgImg}
      {...(isMobile() &&
        game.id &&
        scrollToGameId?.id === game.id?.toString() && { ref: scrollToRef })}
      isLive={isLive}
    >
      <ImageOverlay condition={!!bgImg}>
        <>
          <Header
            selectedGames={selectedGames}
            calendar={calendar}
            whiteText={!!bgImg}
            className={cc([
              gameNumber && !isMobile() ? 'gameIcon' : 'match-card__header',
              {
                'match-card__header--esportPrematchAll':
                  isMobile() && isPrematchEsportsAll
              }
            ])}
          >
            {isSportIconVisible && (
              <GlobalIcon
                lib="sports"
                name={(currentSport || game.sport_alias) as string}
                size={EIconSize._22}
                className="text-color"
              />
            )}
            {selectedGames ? (
              <>
                <TeamCaption
                  size="md"
                  logo={SpringConfigs.TEAM_LOGOS && !props.hideCompetitionLogo}
                  name={game.competitionName}
                  teamId={game.competitionId}
                  teamNumber={ETeamNumber.HOME}
                  competition={true}
                  whiteText={!!bgImg}
                  isNotESport={checkIsNotESportType(game)}
                />
                <Title
                  onClick={onClick}
                  selectedGames={true}
                  whiteText={!!bgImg}
                  className="MatchCardInLine__title"
                >
                  {!!isLive && (
                    <GlobalIcon
                      lib="generic"
                      name="dot"
                      theme="default"
                      size={EIconSize._6}
                      color="#FF4D4F"
                      style={{ marginRight: '4px' }}
                      className="MatchCardInLine__live-dot-icon"
                    />
                  )}
                  {shouldShowLiveTime(!!isLive, currentSport) &&
                    gameCurrentState}
                  {!isLive && startTime}
                </Title>
              </>
            ) : isMobile() && isPrematchEsportsAll ? (
              <>
                {getSelectMarkets()}
                {
                  <span
                    className="esportGameStartTime"
                    data-testid="esport-game-start-time"
                    style={{
                      [`margin-${SpringConfigs.IS_RTL ? 'right' : 'left'}`]:
                        '8px'
                    }}
                  >
                    {startTime}
                  </span>
                }
              </>
            ) : (
              <Title
                onClick={() => {
                  if (props.setShowExpandedMarkets) {
                    props.setShowExpandedMarkets(prev => !prev);
                  } else {
                    onClick();
                  }
                }}
              >
                {isLive && (
                  <GlobalIcon
                    lib="generic"
                    name="dot"
                    theme="default"
                    size={EIconSize._6}
                    color="#FF4D4F"
                    className="MatchCardInLine__live-dot-icon"
                  />
                )}
                <Row
                  align="center"
                  style={{
                    width: '100%',
                    justifyContent: `${!isLive ? 'space-between' : 'start'}`,
                    margin:
                      configs.category === SportType.ESPORT && !isLive
                        ? '0'
                        : '0 4px'
                  }}
                >
                  {calendar && (
                    <span className="calendar-left">
                      <GlobalIcon
                        className="calendar-left-icon"
                        name={`${regionName}`}
                        lib={'flags'}
                        size={EIconSize._20}
                      />
                      {regionName}, {competitionName}
                    </span>
                  )}
                  {shouldShowLiveTime(!!isLive, currentSport) &&
                    gameCurrentState}
                  <Col className="col_time">
                    <span>{!isLive && !calendar && startTime}</span>
                    {!!gameNumber && game.game_number ? (
                      <div
                        className={` ${clicked ? 'bold' : ''} ${
                          !isLive ? 'gameNumber' : 'gameNumberLive'
                        }`}
                        title={t('account.copy')}
                        onClick={event => {
                          event.stopPropagation();
                          copy(`${game.game_number}`);
                          setClicked(true);
                        }}
                      >
                        {t('account.gameNumberId')} {game.game_number}
                      </div>
                    ) : null}
                    {clicked && (
                      <GlobalIcon
                        lib="generic"
                        name="checkcircle"
                        theme="default"
                        size={EIconSize._10}
                        className="check_icon"
                      />
                    )}
                  </Col>
                </Row>
              </Title>
            )}
            <span className="headerIconsGroupMobile">
              {showBetBuilder &&
                (SpringConfigs.HIDE_BET_BUILDER &&
                region === 'Cyprus' ? null : (
                  <Col className="MatchCardInLine__bb-icon-container">
                    <EventAction
                      iconContainerSize={iconSize}
                      icon={
                        <GlobalIcon
                          lib="generic"
                          name="bbIconOutline"
                          theme="default"
                          size={EIconSize._16}
                          className="black-45"
                          onClick={(e: SyntheticEvent) => openBetBuilder(e)}
                        />
                      }
                    />
                  </Col>
                ))}
              {!(isMobile() && isPrematchEsportsAll) && getSelectMarkets()}
              {calendar && (
                <span className="calendar-right">
                  <span className="calendar-right-time">{startTime}</span>
                  {game.hasVideoStream && (
                    <VideoCamIcon
                      size={EIconSize._16}
                      iconContainerSize={iconSize}
                    />
                  )}

                  {SpringConfigs.PUSH_NOTIFICATIONS_ENABLED &&
                    !selectedGames && (
                      <NotificationIcon
                        game={game}
                        currentSport={currentSport}
                        size={EIconSize._16}
                        iconContainerSize={iconSize}
                      />
                    )}
                </span>
              )}
              {game.hasVideoStream && !calendar && (
                <VideoCamIcon
                  size={EIconSize._16}
                  iconContainerSize={iconSize}
                />
              )}
              {SpringConfigs.STATISTICS_SWITCHER && game.is_stat_available && (
                <MatchStatistic
                  matchId={game.id}
                  showStatisticInPopover={!isLive}
                  size={EIconSize._16}
                  iconContainerSize={iconSize}
                  className="statistics__link"
                />
              )}

              {!calendar &&
              SpringConfigs.PUSH_NOTIFICATIONS_ENABLED &&
              !selectedGames ? (
                <NotificationIcon
                  size={EIconSize._16}
                  iconContainerSize={iconSize}
                  game={game}
                  currentSport={currentSport}
                />
              ) : null}
              {!selectedGames && !calendar && (
                <FavoriteButtonGame
                  className="favoriteButtonIcon"
                  size={EIconSize._16}
                  iconContainerSize={iconSize}
                  game={{ id: game.id, type: game.type }}
                />
              )}
              {!selectedGames && (
                <MarketsCount>+{game.markets_count}</MarketsCount>
              )}
              {props.setShowExpandedMarkets && (
                <GlobalIcon
                  onClick={() => props.setShowExpandedMarkets?.(prev => !prev)}
                  color="var(--v3-black-45)"
                  lib="generic"
                  name="caretDownSmall"
                  theme="default"
                  size={EIconSize._20}
                  className={cc([
                    {
                      arrowIconExpanded: props.showExpandedMarkets
                    }
                  ])}
                />
              )}
            </span>
          </Header>
          <Wrapper
            onClick={onClick}
            selectedGames={selectedGames}
            calendar={calendar}
            className="mainContentWrapper"
          >
            <Team>
              <TeamCaption
                size="md"
                logo
                name={game.team1_name}
                teamId={game.team1_id}
                teamNumber={ETeamNumber.HOME}
                isNotESport={checkIsNotESportType(game)}
                whiteText={!!bgImg}
                className={
                  configs.category === SportType.ESPORT
                    ? 'esport__team-name'
                    : ''
                }
              />
              {isLive && game?.info?.score1 && (
                <Pill>
                  {isGamesWithArcheryNewData
                    ? game.info?.additional_data?.matchPointsTotalScore?.team1
                    : game?.info?.score1}
                </Pill>
              )}
            </Team>
            {!isOutRight && game.team2_id && (
              <Team>
                <TeamCaption
                  size="md"
                  logo
                  name={game.team2_name}
                  teamId={game.team2_id}
                  teamNumber={ETeamNumber.AWAY}
                  whiteText={!!bgImg}
                  isNotESport={checkIsNotESportType(game)}
                  className={
                    configs.category === SportType.ESPORT
                      ? 'esport__team-name'
                      : ''
                  }
                />
                {isLive && game?.info?.score2 && (
                  <Pill>
                    {isGamesWithArcheryNewData
                      ? game.info?.additional_data?.matchPointsTotalScore?.team2
                      : game?.info?.score2}
                  </Pill>
                )}
              </Team>
            )}
            {!isOutRight && (
              <div
                className="flexbox justifyEnd oddOverflow"
                style={{
                  marginLeft: -2,
                  marginRight: -2,
                  alignItems: 'inherit'
                }}
              >
                {events?.length ? (
                  <Odds
                    value={selected}
                    gameId={game.id}
                    isBlocked={game.is_blocked}
                    onChange={(
                      eventId: string,
                      price: string,
                      sp?: boolean | undefined,
                      prevSp?: boolean | undefined
                    ) => handleChangeOdds(eventId, price, sp, prevSp)}
                    isLive={isLive}
                    options={events}
                    whiteText={!!bgImg}
                    showHandicaps={notMainMarket}
                    popover={popover}
                    hidePopover={hidePopover}
                    oddFormat={oddFormat}
                  />
                ) : (
                  ''
                )}
              </div>
            )}
            {isLive && !selectedGames && game.text_info && (
              <div className="statistics">{game.text_info}</div>
            )}
          </Wrapper>
        </>
      </ImageOverlay>
    </Container>
  );
});
